import EmailHtmlPreview from '@/components/affiliate/bulk-email/EmailHtmlPreview';
import ContentHeader from '@/components/messaging/ContentHeader';
import { Avatar } from '@/components/ui/avatar';
import { useSentBatchDetailsQuery } from '@/hooks/queries/use-sent-batch-details-query';
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Separator,
  Stat,
  StatGroup,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { NavArrowLeft } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { EMAIL_STATUS_COLOR } from '../constants';
import SentBatchDetailSkeleton from './SentBatchDetailSkeleton';

const SentBatchDetail: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { batchId } = useParams<{ batchId: string }>();
  const { data, isLoading, isError, error } = useSentBatchDetailsQuery(batchId ?? null);

  const handleBack = () => {
    navigate('/messages?messageFilter=sent');
  };

  if (isLoading) {
    return <SentBatchDetailSkeleton />;
  }

  if (isError || !data) {
    return (
      <Flex direction="column" h="full" align="center" justify="center" gap={3} p={6}>
        <Text color="red.500" fontSize="sm">
          {error?.message || t('messages.errorLoading')}
        </Text>
        <Button variant="ghost" size="sm" onClick={handleBack}>
          {t('common.back')}
        </Button>
      </Flex>
    );
  }

  const { batch, items } = data;

  return (
    <Flex direction="column" h="full" overflow="hidden">
      <ContentHeader>
        <HStack gap={2} w="full">
          <Button variant="ghost" size="sm" onClick={handleBack} p={1} minW="auto">
            <NavArrowLeft width={18} height={18} />
          </Button>
          <Box flex={1} minW={0}>
            <Text fontWeight="semibold" fontSize="md" truncate>
              {batch.subject}
            </Text>
            {batch.programName && (
              <Text fontSize="xs" color="gray.500">
                {batch.programName}
              </Text>
            )}
          </Box>
        </HStack>
      </ContentHeader>

      {/* Scrollable content */}
      <Box flex={1} overflowY="auto" px={5} py={4}>
        <VStack align="stretch" gap={5} maxW="7xl" mx="auto" w="full">
          {/* Stats */}
          <StatGroup
            borderWidth="1px"
            borderColor="gray.200"
            rounded="lg"
            overflow="hidden"
            bg="white"
          >
            <Stat.Root p={3} textAlign="center">
              <Stat.Label>{t('messages.sentTab.recipients')}</Stat.Label>
              <Stat.ValueText>{batch.totalCount}</Stat.ValueText>
            </Stat.Root>
            <Stat.Root p={3} textAlign="center" borderLeftWidth="1px" borderColor="gray.200">
              <Stat.Label>{t('messages.email.sent')}</Stat.Label>
              <Stat.ValueText>{batch.sentCount}</Stat.ValueText>
            </Stat.Root>
            <Stat.Root p={3} textAlign="center" borderLeftWidth="1px" borderColor="gray.200">
              <Stat.Label>{t('messages.email.failed')}</Stat.Label>
              <Stat.ValueText>{batch.failedCount}</Stat.ValueText>
            </Stat.Root>
          </StatGroup>

          {/* Email preview */}
          <Box borderWidth="1px" borderColor="gray.200" rounded="lg" overflow="hidden" bg="white">
            <VStack align="stretch" gap={0} px={4} py={3}>
              <HStack fontSize="sm" gap={1}>
                <Text color="gray.500" flexShrink={0}>
                  {t('messages.sentTab.emailMeta.from')}:
                </Text>
                <Text fontWeight="medium" truncate>
                  {batch.senderName || batch.senderEmail}
                </Text>
              </HStack>
              <HStack fontSize="sm" gap={1}>
                <Text color="gray.500" flexShrink={0}>
                  {t('messages.sentTab.emailMeta.to')}:
                </Text>
                <Text truncate>
                  {t('messages.sentTab.recipientCount', { count: batch.totalCount })}
                </Text>
              </HStack>
              <HStack fontSize="sm" gap={1}>
                <Text color="gray.500" flexShrink={0}>
                  {t('messages.sentTab.emailMeta.subject')}:
                </Text>
                <Text fontWeight="medium" truncate>
                  {batch.subject}
                </Text>
              </HStack>
              {batch.programName && (
                <HStack fontSize="sm" gap={1}>
                  <Text color="gray.500" flexShrink={0}>
                    {t('messages.sentTab.emailMeta.program')}:
                  </Text>
                  <Text truncate>{batch.programName}</Text>
                </HStack>
              )}
            </VStack>
            <Separator borderColor="gray.200" />
            {batch.body && <EmailHtmlPreview html={batch.body} px={4} py={3} />}
          </Box>

          {/* Recipients table */}
          <Box>
            <Text
              fontSize="xs"
              fontWeight="semibold"
              color="gray.500"
              textTransform="uppercase"
              letterSpacing="wide"
              mb={2}
            >
              {t('messages.sentTab.recipients')}
            </Text>
            <Table.Root size="md">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>
                    {t('messages.sentTab.recipientTable.name')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    {t('messages.sentTab.recipientTable.email')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="center">
                    {t('messages.sentTab.recipientTable.status')}
                  </Table.ColumnHeader>
                  <Table.ColumnHeader textAlign="right">
                    {t('messages.sentTab.recipientTable.sentAt')}
                  </Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {items.data.map((item) => (
                  <Table.Row key={item.id}>
                    <Table.Cell>
                      <HStack gap={2}>
                        <Avatar
                          size="2xs"
                          name={item.recipientName || '?'}
                          src={item.avatarUrl || undefined}
                          fallback={(item.recipientName || '?').charAt(0).toUpperCase()}
                          colorPalette="primary"
                          variant="subtle"
                        />
                        <Text fontSize="sm">{item.recipientName || '-'}</Text>
                      </HStack>
                    </Table.Cell>
                    <Table.Cell>
                      <Text fontSize="sm" color="gray.500">
                        {item.recipientEmail}
                      </Text>
                    </Table.Cell>
                    <Table.Cell textAlign="center">
                      <Badge
                        size="xs"
                        variant="subtle"
                        colorPalette={EMAIL_STATUS_COLOR[item.status] || 'gray'}
                      >
                        {item.status}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      <Text fontSize="xs" color="gray.500">
                        {item.sentAt ? new Date(item.sentAt).toLocaleString() : '-'}
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
          </Box>
        </VStack>
      </Box>
    </Flex>
  );
};

export default SentBatchDetail;
